// BMR pre-print check. Mounted once in the layout; opened when a BMR print
// comes back 428 BMR_PREFLIGHT_REQUIRED (utils/exportOpen dispatches the
// event). The person confirms MRP and pack configuration for THIS batch —
// prefilled from the SKU master, so an unchanged SKU is one click — and the
// server writes any change back to the SKU. Accept re-runs the print from the
// click itself, so the new tab is not popup-blocked.
import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { openProtectedExport } from '../utils/exportOpen';
import { T, AoneButton, AoneInput } from '../design-system';

export const BMR_PREFLIGHT_EVENT = 'aone:bmr-preflight';
const PACK_TYPES = ['bottle', 'jar', 'tube', 'pouch', 'sachet', 'spray bottle', 'roll-on', 'stick', 'box'];
const CLOSURES = ['cap', 'flip-top cap', 'disc-top cap', 'pump', 'spray', 'dropper', 'roll-on ball', 'screw lid', 'none'];

const inr = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const when = (d) => new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function BmrPreflightHost() {
  const [req, setReq] = useState(null);   // { taskId, url, opts }
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [problems, setProblems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    const on = (e) => { setReq(e.detail); setData(null); setForm(null); setProblems([]); setLoadErr(''); };
    window.addEventListener(BMR_PREFLIGHT_EVENT, on);
    return () => window.removeEventListener(BMR_PREFLIGHT_EVENT, on);
  }, []);

  useEffect(() => {
    if (!req?.taskId) return;
    api.get(`/pdf/bmr/${encodeURIComponent(req.taskId)}/preflight`)
      .then(r => {
        const v = r.data.values || {};
        setData(r.data);
        setProblems(r.data.problems || []);
        setForm({ mrp: v.mrp > 1 ? String(v.mrp) : '', packType: v.packType || '', components: v.components || '', unitsPerCarton: v.unitsPerCarton ? String(v.unitsPerCarton) : '' });
      })
      .catch(e => setLoadErr(e.response?.data?.error || 'Could not load the BMR check'));
  }, [req]);

  if (!req) return null;
  const close = () => { if (!saving) setReq(null); };
  const v = data?.values || {};
  const unchanged = form && String(form.mrp) === String(v.mrp ?? '') && form.packType === (v.packType || '')
    && form.components === (v.components || '') && String(form.unitsPerCarton) === String(v.unitsPerCarton ?? '');
  const cost = data?.cost?.unitCost;
  const mrpNum = Number(form?.mrp);
  const belowCost = cost > 0 && mrpNum > 0 && mrpNum < cost;
  const problemFor = (f) => problems.find(p => p.field === f)?.message;

  const accept = async () => {
    const { url, opts = {} } = req;
    // Open the print tab now, inside the click — after the save's await the
    // browser would treat it as a popup and block it.
    const w = opts.download ? null : window.open('', '_blank');
    setSaving(true); setProblems([]);
    try {
      await api.post(`/pdf/bmr/${encodeURIComponent(req.taskId)}/preflight`, form);
      setReq(null);
      openProtectedExport(url, { ...opts, win: w });
    } catch (e) {
      if (w) w.close();
      setProblems(e.response?.data?.problems || [{ field: null, message: e.response?.data?.error || 'Could not save' }]);
    }
    setSaving(false);
  };

  const field = (key, label, input) => (
    <div>
      {input}
      {problemFor(key) && <div style={{ color: T.danger, fontSize: T.type.xs, marginTop: 4 }}>{problemFor(key)}</div>}
    </div>
  );

  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="BMR pre-print check"
        style={{ background: T.surface, borderRadius: 14, width: 'min(520px, 100%)', maxHeight: '92vh', overflowY: 'auto', padding: 22, fontFamily: T.font, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: T.type.xxs, fontWeight: 800, color: T.textSec, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Before this BMR prints</div>
        {!data && !loadErr && <div style={{ padding: '24px 0', color: T.textTer, fontSize: T.type.sm }}>Loading…</div>}
        {loadErr && <div style={{ padding: '16px 0', color: T.danger, fontSize: T.type.sm }}>{loadErr}</div>}
        {data && form && (
          <>
            <h3 style={{ margin: '4px 0 2px', fontSize: T.type.md, fontWeight: 800, color: T.text }}>{data.sku?.code} — {data.sku?.name}</h3>
            <div style={{ fontSize: T.type.xs, color: T.textSec, marginBottom: 14 }}>
              BMR {data.taskLabel}. Check these against the pack and the price list; whatever you accept is saved to the SKU master.
            </div>
            {data.last && (
              <div style={{ fontSize: T.type.xs, color: data.last.unchanged ? T.success : T.textSec, background: data.last.unchanged ? T.successBg : T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}>
                Last checked {when(data.last.at)}{data.last.by ? ` by ${data.last.by}` : ''}{data.last.taskLabel ? ` for ${data.last.taskLabel}` : ''}
                {data.last.unchanged ? ' — nothing has changed since. Accept to move on.' : ' — the SKU has changed since; look again.'}
              </div>
            )}
            <div style={{ display: 'grid', gap: 12 }}>
              {field('mrp', 'MRP', (
                <AoneInput label="MRP (₹, incl. GST)" required type="number" value={form.mrp} onChange={x => setForm(f => ({ ...f, mrp: x }))} placeholder="e.g. 499" />
              ))}
              <div style={{ fontSize: T.type.xs, color: belowCost ? T.danger : T.textSec, marginTop: -6 }}>
                Cost of one unit: <b>{inr(cost)}</b>
                {data.cost?.basis && data.cost.basis !== 'full' ? ` (${data.cost.basis === 'sku_manual' ? 'manual cost on the SKU' : 'incomplete — real cost is higher'})` : ''}
                {cost == null ? ' — cannot be computed (no costed formulation); MRP is checked as filled only' : ''}
                {cost > 0 && mrpNum > 0 ? ` · MRP is ${(mrpNum / cost).toFixed(1)}× cost` : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {field('packType', 'Pack type', (
                  <>
                    <AoneInput label="Pack type" required value={form.packType} onChange={x => setForm(f => ({ ...f, packType: x }))} list="bmr-pf-pack" placeholder="bottle" />
                    <datalist id="bmr-pf-pack">{PACK_TYPES.map(p => <option key={p} value={p} />)}</datalist>
                  </>
                ))}
                {field('components', 'Closure', (
                  <>
                    <AoneInput label="Closure / component" required value={form.components} onChange={x => setForm(f => ({ ...f, components: x }))} list="bmr-pf-closure" placeholder="pump" />
                    <datalist id="bmr-pf-closure">{CLOSURES.map(p => <option key={p} value={p} />)}</datalist>
                  </>
                ))}
              </div>
              {field('unitsPerCarton', 'Units per carton', (
                <AoneInput label="Units per carton" required type="number" value={form.unitsPerCarton} onChange={x => setForm(f => ({ ...f, unitsPerCarton: x }))} placeholder="e.g. 48" />
              ))}
            </div>
            {problems.filter(p => !p.field).map((p, i) => <div key={i} style={{ color: T.danger, fontSize: T.type.xs, marginTop: 10 }}>{p.message}</div>)}
            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <AoneButton variant="primary" onClick={accept} disabled={saving || belowCost}>
                {saving ? 'Saving…' : unchanged ? 'Accept & print' : 'Save to SKU & print'}
              </AoneButton>
              <AoneButton variant="secondary" onClick={close} disabled={saving}>Cancel</AoneButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
